import {
  ArrowRight, Package, Briefcase, Building2, BookOpen, ClipboardCheck, CreditCard,
  FileCheck2, FileText, Globe, GraduationCap, Layers, Megaphone, MessageCircle,
  MessageSquare, NotebookPen, Palette, PhoneCall, PlayCircle, Presentation,
  Receipt, Sparkles, UserCog, UserPlus, Video,
} from "lucide-react";

/**
 * The Action Center's two lookup tables: what a signal LOOKS like, and what it
 * is ABOUT. Both are keyed by strings the server sends, and both exist as
 * literal maps rather than interpolated strings for the same two reasons.
 *
 * ⚠️ **A Tailwind class cannot be built at runtime.** The compiler scans source
 * text for whole class names; `` `tw-bg-${tone}/10` `` is never seen, emits no
 * CSS, and the chip renders untinted — with no error anywhere. Every class here
 * is written out in full for that reason. (`verify:tw` proves a class emits;
 * nothing proves a class that was never written was meant to exist.)
 *
 * ⚠️ **And the tones are the SEMANTIC families, not a palette.** The question at
 * each call site is never "what colour is this row", it is what the row means:
 * `danger` = somebody is blocked right now, `warning` = this is drifting and
 * will cost money later, `info` = worth knowing, `brand` = an action or a
 * feature, which is `--primary`'s job. There is no fifth option, because there
 * is no fifth meaning.
 */
export const TONE_CHIP = {
  danger: "tw-bg-danger/10 tw-text-danger",
  warning: "tw-bg-warning/10 tw-text-warning",
  info: "tw-bg-info/10 tw-text-info",
  success: "tw-bg-success/10 tw-text-success",
  brand: "tw-bg-primary/10 tw-text-primary",
};

/** The count pill that carries the number. Same families, one step softer. */
export const TONE_PILL = {
  danger: "tw-bg-danger/15 tw-text-danger",
  warning: "tw-bg-warning/15 tw-text-warning",
  info: "tw-bg-info/15 tw-text-info",
  success: "tw-bg-success/15 tw-text-success",
  brand: "tw-bg-primary/15 tw-text-primary",
};

/** The hairline at the top of the card, so urgency is legible before reading. */
export const TONE_RULE = {
  danger: "tw-bg-danger",
  warning: "tw-bg-warning",
  info: "tw-bg-info",
  success: "tw-bg-success",
  brand: "tw-bg-primary",
};

export const toneOf = (tone) => (TONE_CHIP[tone] ? tone : "info");

/**
 * Icons are the category, and the category is what makes a list of ten rows
 * scannable without reading any of them. Keyed by the server's `icon` field so
 * a new signal picks its glyph in the catalogue, not here.
 */
export const SIGNAL_ICONS = {
  // setup
  course: BookOpen,
  batch: Layers,
  instructor: UserCog,
  student: GraduationCap,
  branding: Palette,
  live: Video,
  payments: CreditCard,
  // attention
  enquiry: MessageSquare,
  followup: PhoneCall,
  enrollment: UserPlus,
  fees: Receipt,
  whatsapp: MessageCircle,
  demo: Presentation,
  billing: Sparkles,
  // discover
  leads: Megaphone,
  assessment: ClipboardCheck,
  exam: FileCheck2,
  selfPaced: PlayCircle,
  form: FileText,
  website: Globe,
  interview: Briefcase,
  assignment: NotebookPen,
  branch: Building2,
  asset: Package,
};

export const iconFor = (key) => SIGNAL_ICONS[key] || ArrowRight;
